import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Upload, Folder, HardDrive, Loader2, X, ArrowUp } from 'lucide-react';
import { IconButton } from '../../components/ui/PanelControls';
import PillDropdown from '../../components/ui/PillDropdown';
import {
  searchAssetCandidates,
  attachAsset,
  uploadAssets,
  createAsset,
} from '../editor/shortformEditor/assetClient';
import { getDriveStatus, getDriveAuthUrl, getDriveAccessToken, openDrivePicker, downloadDriveFileAsFile, isPickerConfigured } from './googleDriveClient';

export const KIND_PRESETS = ['character-ref', 'location-ref', 'style-ref', 'prop-ref', 'brand-file', 'voice-ref', 'transition-ref'];

// 'voice-ref' assets are audio, not pictures -- every other kind (including
// 'transition-ref': a short clip or couple of storyboard-style images
// illustrating an edit transition -- whip pan, match cut, dissolve, etc.) is
// image-or-video. Kept as a single well-known exception rather than a
// general content-type system, since it's the only audio kind today (see
// openspec/changes/character-voice-library-and-oneshot-audiobook).
export const isAudioKind = (kind) => kind === 'voice-ref';

// 'contract' assets are a signed PDF filed by the DocuSign webhook
// (server/routes/webhooks.js), and 'guideline' assets are a brand-guideline/
// style-reference document attached from a shortform file's Brief panel
// (openspec/changes/shortform-brief-context-generation) -- neither is a
// picture, so the library renders a document tile + "Open" instead of an
// <img>. Same single-well-known-exception approach as isAudioKind above.
export const isDocumentAsset = (asset) =>
  asset?.kind === 'contract' || asset?.kind === 'guideline' || /\.pdf(\?|$)/i.test(asset?.imageUrl || asset?.imageKey || '');

/** Compact popover (not a permanent inline block) for creating one asset --
 * entity link + kind + label, then search/upload/link. Shared by
 * AssetLibraryContent.jsx (free entity picker) and StoryLedgerPanel.jsx
 * (entity already known -- pass `lockedEntityId` to skip the picker
 * entirely and attach straight to that entity).
 *
 * Mode picker is the same segmented-track control (single bordered track,
 * inverted-fill active segment) Storyboard's composer and Story Ledger's
 * entry-type picker use, replacing a grid of separately-bordered pill
 * buttons -- one consistent "choose one of these" control instead of a
 * different-looking one per surface. Text inputs sit in a recessed `c.hover`
 * well (this component's palette -- PanelControls.tint(), the same strict
 * black/white family the Sources/Chat/Artifact panels use -- has no
 * separate canvas/well token of its own, so `hover` doubles as the closest
 * "recessed" tone already defined here) with a circular submit button,
 * matching Storyboard's prompt-row shape instead of a plain rectangular
 * input + small "Go" pill.
 *
 * No "Generate" mode -- removed per direct request, along with every
 * Sparkles icon in the app.
 */
export const AddAssetPopover = ({ isDarkMode, c, projectId, entities = [], defaultKind, defaultMode = 'search', lockedEntityId = null, onClose, onCreated }) => {
  const [entityId, setEntityId] = useState(lockedEntityId || '');
  const [kind, setKind] = useState(defaultKind);
  const [label, setLabel] = useState('');
  const [isCanonical, setIsCanonical] = useState(true);
  // 'search' | 'upload' | 'link' | 'drive' -- callers attaching something
  // genery.io/eyecandy image search can't help with (a guideline document,
  // for instance) pass defaultMode="upload" to skip straight past it.
  const [mode, setMode] = useState(defaultMode);
  const [query, setQuery] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busyUrl, setBusyUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [formError, setFormError] = useState(null);
  const uploadInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => { if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onClose]);

  // 'voice-ref' only offers Upload/Drive (see the mode track below) -- if
  // the user had search selected before switching the kind picker to
  // voice-ref, land on Upload instead of showing an empty pane.
  useEffect(() => {
    if (isAudioKind(kind) && mode === 'search') setMode('upload');
  }, [kind, mode]);

  // The entity/kind pickers below use PillDropdown (this app's one dropdown
  // design, not a native <select>/<input list>, both of which ignore
  // authored background/color in some browsers and render pure white
  // regardless of dark mode -- confirmed live). Plain text inputs are
  // unaffected, so this style is still fine for the Label field beside them.
  const inputStyle = {
    width: '100%', padding: '6px 8px', borderRadius: '5px', border: `1px solid ${c.border}`,
    background: c.bg, color: c.text, fontSize: '12px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };
  // Recessed "well" treatment for the mode panel's own prompt-style row --
  // the EXACT same values StoryboardFloatingControls' composer well uses
  // (rgba(255,255,255,0.05) lift in dark mode / a distinct tone in light,
  // 1px border, 10px radius/padding, 8px gap, a 30px circular submit
  // button), not an approximation -- this component's tint() palette has no
  // separate canvas/well token of its own, so `c.hover` stands in for
  // Storyboard's `tokens.canvasBg` in light mode specifically because it's
  // the one already-defined tone here that reads as "recessed" against
  // `c.bg`, same role canvasBg plays there.
  const wellStyle = {
    display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '10px', padding: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : c.hover,
    border: `1px solid ${c.border}`,
  };
  const wellInputStyle = { flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: c.text, fontSize: '12px', fontFamily: 'inherit', lineHeight: 1.5, padding: 0 };
  const circularBtnStyle = {
    flexShrink: 0, width: '30px', height: '30px', borderRadius: '50%', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    backgroundColor: c.fg, color: c.bg,
  };

  const resetAfterCreate = () => { setResults(null); setQuery(''); setLinkUrl(''); setLabel(''); };

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    setFormError(null);
    try {
      setResults(await searchAssetCandidates(query.trim()));
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const handlePick = useCallback(async (candidate) => {
    if (!label.trim() || busyUrl) return;
    setBusyUrl(candidate.imageUrl);
    setFormError(null);
    try {
      await attachAsset({
        projectId, entityId: entityId || null, isCanonical: !!entityId && isCanonical,
        label: label.trim(), kind, selectedImageUrl: candidate.imageUrl, sourceNotes: candidate.notes,
      });
      resetAfterCreate();
      onCreated();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusyUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, kind, entityId, isCanonical, projectId, busyUrl, onCreated]);

  const handleUpload = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setFormError(null);
    try {
      await uploadAssets({ files, projectId, entityId: entityId || null, isCanonical: !!entityId && isCanonical, kind });
      resetAfterCreate();
      onCreated();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, entityId, isCanonical, projectId, onCreated]);

  // "Link" mode -- a plain pasted URL (a direct video/image file URL, or a
  // YouTube/Vimeo/Drive watch-page link), no upload/re-hosting at all.
  // createAsset() -> POST /api/assets records the Asset row pointing
  // straight at the given URL (`imageKey` stores it verbatim -- see
  // server/routes/assets.js's withResolvedUrl doc comment on the "full
  // public URL" half of its dual storage convention), same mechanism a
  // search-sourced asset already uses, just with a human-supplied URL
  // instead of one this popover's own search flow produced.
  const handleAttachLink = useCallback(async () => {
    if (!label.trim() || !linkUrl.trim()) return;
    setLinking(true);
    setFormError(null);
    try {
      await createAsset({
        projectId, entityId: entityId || null, isCanonical: !!entityId && isCanonical,
        label: label.trim(), kind, imageUrl: linkUrl.trim(), source: 'external_link',
      });
      resetAfterCreate();
      onCreated();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setLinking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, kind, linkUrl, entityId, isCanonical, projectId, onCreated]);

  // Google Drive import -- reuses the existing server-side OAuth flow
  // (server/routes/google-drive.js) via googleDriveClient.js. driveStage
  // walks: 'checking' -> 'not_configured' | 'disconnected' | 'ready'.
  const [driveStage, setDriveStage] = useState('checking');
  const [connectingDrive, setConnectingDrive] = useState(false);
  const [importingDrive, setImportingDrive] = useState(false);
  const driveStatusPollRef = useRef(null);

  const refreshDriveStatus = useCallback(async () => {
    try {
      const status = await getDriveStatus();
      if (!status.configured || !isPickerConfigured()) { setDriveStage('not_configured'); return status; }
      setDriveStage(status.connected ? 'ready' : 'disconnected');
      return status;
    } catch {
      setDriveStage('not_configured');
      return null;
    }
  }, []);

  useEffect(() => {
    if (mode !== 'drive') return undefined;
    refreshDriveStatus();
    return () => { if (driveStatusPollRef.current) clearInterval(driveStatusPollRef.current); };
  }, [mode, refreshDriveStatus]);

  const handleConnectDrive = useCallback(async () => {
    setConnectingDrive(true);
    setFormError(null);
    try {
      const url = await getDriveAuthUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
      // Poll until the popup completes the OAuth round trip (no postMessage
      // channel back from that tab -- same "poll status" approach the
      // desktop app's own Drive-connect flow already documents server-side).
      driveStatusPollRef.current = setInterval(async () => {
        const status = await refreshDriveStatus();
        if (status?.connected) {
          clearInterval(driveStatusPollRef.current);
          setConnectingDrive(false);
        }
      }, 3000);
    } catch (err) {
      setFormError(err.message);
      setConnectingDrive(false);
    }
  }, [refreshDriveStatus]);

  const handleImportFromDrive = useCallback(async () => {
    if (!label.trim()) { setFormError('Enter a label first'); return; }
    setImportingDrive(true);
    setFormError(null);
    try {
      const accessToken = await getDriveAccessToken();
      const docs = await openDrivePicker({ accessToken });
      if (!docs || docs.length === 0) return; // cancelled
      const files = await Promise.all(docs.map((doc) => downloadDriveFileAsFile(doc, accessToken)));
      await uploadAssets({ files, projectId, entityId: entityId || null, isCanonical: !!entityId && isCanonical, kind });
      resetAfterCreate();
      onCreated();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setImportingDrive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, kind, entityId, isCanonical, projectId, onCreated]);

  const modes = [
    ...(!isAudioKind(kind) ? [{ id: 'search', label: 'Search' }] : []),
    { id: 'upload', label: 'Upload' },
    { id: 'link', label: 'Link' },
    { id: 'drive', label: 'Drive' },
  ];

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'absolute', top: '100%', right: 0, marginTop: '4px', zIndex: 30,
        width: '300px', maxHeight: '420px', overflowY: 'auto', padding: '12px', borderRadius: '10px',
        backgroundColor: c.bg, border: `1px solid ${c.border}`,
        boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.7)' : '0 8px 24px rgba(0,0,0,0.12)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, flex: 1 }}>Add asset</span>
        <IconButton isDarkMode={isDarkMode} size={20} onClick={onClose} title="Close">
          <X size={13} />
        </IconButton>
      </div>

      {!lockedEntityId && (
        <div style={{ marginBottom: '6px' }}>
          <PillDropdown
            value={entityId}
            options={[{ value: '', label: 'No entity (general asset)' }, ...entities.map((e) => ({ value: e.id, label: `${e.entityName} (${e.entityType})` }))]}
            onChange={setEntityId}
            isDarkMode={isDarkMode}
          />
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
        <PillDropdown value={kind} options={KIND_PRESETS} onChange={setKind} isDarkMode={isDarkMode} allowCustom />
        <input style={inputStyle} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" />
      </div>
      {entityId && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: c.secondary, marginBottom: '8px' }}>
          <input type="checkbox" checked={isCanonical} onChange={(e) => setIsCanonical(e.target.checked)} />
          Make canonical
        </label>
      )}

      {/* Segmented track -- same control Storyboard's composer and Story
          Ledger's entry-type picker use, not a grid of separately-bordered
          pill buttons. */}
      <div style={{ display: 'flex', gap: '2px', padding: '3px', borderRadius: '8px', background: c.hover, marginBottom: '8px' }}>
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            style={{
              flex: 1, padding: '6px 4px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
              border: 'none', background: mode === m.id ? c.fg : 'transparent',
              color: mode === m.id ? c.bg : c.secondary,
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'search' && !isAudioKind(kind) && (
        <div>
          <div style={wellStyle}>
            <input
              style={wellInputStyle} value={query} placeholder="Search a reference…"
              onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <button type="button" onClick={handleSearch} disabled={!query.trim() || searching} style={{ ...circularBtnStyle, opacity: !query.trim() || searching ? 0.5 : 1 }}>
              {searching ? <Loader2 size={14} className="spin" /> : <Search size={14} />}
            </button>
          </div>
          {results && (() => {
            const candidates = [...(results.genery || []), ...(results.eyecandy || [])];
            if (candidates.length === 0) return <div style={{ fontSize: '11px', color: c.secondary, marginTop: '8px' }}>No results found.</div>;
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {candidates.map((candidate, idx) => (
                  <button
                    key={`${candidate.imageUrl}-${idx}`} type="button" onClick={() => handlePick(candidate)}
                    disabled={!label.trim() || !!busyUrl} title={label.trim() ? candidate.notes : 'Enter a label first'}
                    style={{ width: '50px', height: '50px', padding: 0, borderRadius: '5px', overflow: 'hidden', border: `1px solid ${c.border}`, cursor: busyUrl ? 'wait' : 'pointer', opacity: busyUrl && busyUrl !== candidate.imageUrl ? 0.5 : 1, position: 'relative', background: 'transparent' }}
                  >
                    <img src={candidate.imageUrl} alt={candidate.notes || 'reference'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {busyUrl === candidate.imageUrl && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
                        <Loader2 size={14} color="#fff" className="spin" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {mode === 'upload' && (
        <div style={{ display: 'flex', gap: '6px' }}>
          {/* Non-audio kinds (including 'transition-ref') accept video too --
              server/routes/assets.js's ALLOWED_VIDEO_TYPES/fileFilter is the
              real gate; this is just the browser file-picker's own filter. */}
          <input ref={uploadInputRef} type="file" accept={isAudioKind(kind) ? 'audio/*' : 'image/*,video/*'} multiple style={{ display: 'none' }} onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }} />
          <button
            type="button" disabled={uploading} onClick={() => uploadInputRef.current?.click()}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', borderRadius: '8px', border: `1px solid ${c.border}`, background: 'transparent', color: c.text, fontSize: '11px', fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}
          >
            {uploading ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
            {uploading ? 'Uploading…' : 'File(s)'}
          </button>
          {!isAudioKind(kind) && (
            <input
              ref={folderInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }}
              // eslint-disable-next-line react/no-unknown-property -- non-standard but widely supported; Firefox falls back to a normal multi-file picker
              webkitdirectory="" directory=""
              onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
            />
          )}
          {!isAudioKind(kind) && (
            <button
              type="button" disabled={uploading} onClick={() => folderInputRef.current?.click()}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', borderRadius: '8px', border: `1px solid ${c.border}`, background: 'transparent', color: c.text, fontSize: '11px', fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}
            >
              <Folder size={13} /> Folder
            </button>
          )}
        </div>
      )}

      {mode === 'link' && (
        <div>
          <div style={wellStyle}>
            <input
              style={wellInputStyle} value={linkUrl} placeholder="Paste a video/image URL, YouTube, Vimeo, or Drive link…"
              onChange={(e) => setLinkUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAttachLink(); }}
            />
            <button type="button" onClick={handleAttachLink} disabled={!label.trim() || !linkUrl.trim() || linking} style={{ ...circularBtnStyle, opacity: !label.trim() || !linkUrl.trim() || linking ? 0.5 : 1 }}>
              {linking ? <Loader2 size={14} className="spin" /> : <ArrowUp size={15} />}
            </button>
          </div>
          <div style={{ fontSize: '10px', color: c.secondary, marginTop: '6px' }}>
            No upload -- streamed/embedded straight from the link at preview time.
          </div>
        </div>
      )}

      {mode === 'drive' && (
        <div>
          {driveStage === 'checking' && (
            <div style={{ fontSize: '11px', color: c.secondary }}>Checking Google Drive connection…</div>
          )}
          {driveStage === 'not_configured' && (
            <div style={{ fontSize: '11px', color: c.secondary }}>
              Google Drive import isn't set up for this app yet (needs GOOGLE_DRIVE_CLIENT_ID/SECRET on the server and REACT_APP_GOOGLE_PICKER_API_KEY here).
            </div>
          )}
          {driveStage === 'disconnected' && (
            <button
              type="button" disabled={connectingDrive} onClick={handleConnectDrive}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '9px', borderRadius: '8px', border: `1px solid ${c.border}`, background: 'transparent', color: c.text, fontSize: '11px', fontWeight: 600, cursor: connectingDrive ? 'wait' : 'pointer' }}
            >
              {connectingDrive ? <Loader2 size={13} className="spin" /> : <HardDrive size={13} />}
              {connectingDrive ? 'Waiting for connection…' : 'Connect Google Drive'}
            </button>
          )}
          {driveStage === 'ready' && (
            <button
              type="button" disabled={importingDrive} onClick={handleImportFromDrive}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '9px', borderRadius: '8px', border: `1px solid ${c.border}`, background: 'transparent', color: c.text, fontSize: '11px', fontWeight: 600, cursor: importingDrive ? 'wait' : 'pointer' }}
            >
              {importingDrive ? <Loader2 size={13} className="spin" /> : <HardDrive size={13} />}
              {importingDrive ? 'Importing…' : 'Choose from Drive'}
            </button>
          )}
        </div>
      )}

      {formError && <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '8px' }}>{formError}</div>}
    </div>
  );
};

export default AddAssetPopover;
